/**
 * Messages Badge Hook - Hook para obtener contador de mensajes no leídos
 * Actualiza automáticamente cada 30 segundos
 */

import { useState, useEffect } from 'react';
import { eplApi } from '../epl_modules/services/epl_api';

// Poller compartido para evitar requests duplicadas si el hook se usa en múltiples componentes.
let sharedUnreadCount = 0;
let sharedLoading = false;
let pollTimerId = null;
let inFlightPromise = null;
let subscriberCount = 0;
const subscribers = new Set();

const BASE_INTERVAL_MS = 30000;
const ERROR_BACKOFF_MS = 120000;
let currentIntervalMs = BASE_INTERVAL_MS;
let pollingDisabledDueToError = false;

const notifySubscribers = () => {
  for (const fn of subscribers) fn({ unreadCount: sharedUnreadCount, loading: sharedLoading });
};

const stopPollingIfIdle = () => {
  if (subscriberCount > 0) return;
  if (pollTimerId) {
    clearTimeout(pollTimerId);
    pollTimerId = null;
  }
  inFlightPromise = null;
  sharedLoading = false;
  pollingDisabledDueToError = false;
};

const scheduleNextPoll = (delayMs) => {
  if (pollTimerId) clearTimeout(pollTimerId);
  pollTimerId = setTimeout(() => {
    void pollUnreadCount();
  }, delayMs);
};

const pollUnreadCount = async ({ force = false } = {}) => {
  if (subscriberCount === 0) {
    stopPollingIfIdle();
    return;
  }
  if (pollingDisabledDueToError && !force) return null;
  if (inFlightPromise) return inFlightPromise;

  sharedLoading = true;
  notifySubscribers();

  inFlightPromise = (async () => {
    try {
      const response = await eplApi.getUnreadMessagesCount();
      sharedUnreadCount = response?.count || 0;
      currentIntervalMs = BASE_INTERVAL_MS;
      pollingDisabledDueToError = false;
    } catch (_error) {
      // Silencioso: el backend EPL (3002) puede no estar disponible.
      sharedUnreadCount = 0;
      pollingDisabledDueToError = true;
      currentIntervalMs = ERROR_BACKOFF_MS;
    } finally {
      sharedLoading = false;
      notifySubscribers();
      inFlightPromise = null;
      if (!pollingDisabledDueToError) {
        scheduleNextPoll(currentIntervalMs);
      }
    }
  })();

  return inFlightPromise;
};

export const useUnreadMessages = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onUpdate = (state) => {
      setUnreadCount(state.unreadCount);
      setLoading(state.loading);
    };

    subscribers.add(onUpdate);
    subscriberCount += 1;

    // Emitir estado actual inmediatamente
    onUpdate({ unreadCount: sharedUnreadCount, loading: sharedLoading });

    // Poll inmediato al montar el primer subscriber
    if (subscriberCount === 1) {
      currentIntervalMs = BASE_INTERVAL_MS;
      pollingDisabledDueToError = false;
      scheduleNextPoll(0);
    }

    return () => {
      subscribers.delete(onUpdate);
      subscriberCount = Math.max(0, subscriberCount - 1);
      stopPollingIfIdle();
    };
  }, []);

  return { unreadCount, loading, refetch: () => pollUnreadCount({ force: true }) };
};
